import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldAlert } from "lucide-react";

// Blocking gate shown when an admin has required the user to set a new password
// before continuing. It cannot be dismissed until the change succeeds.
export function ForcePasswordChange() {
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const open = !!(user && user.must_change_password && !user.preview);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters.");
    if (next !== confirm) return toast.error("New passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        data-testid="force-change-password-dialog"
        className="[&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl text-[#003057]">
            <ShieldAlert className="h-5 w-5 text-amber-500" /> Update your password
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          A Website Administrator set a temporary password for your account. Choose a new
          password to continue.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="fpc-current">Temporary password</Label>
            <Input id="fpc-current" data-testid="fpc-current" type="password" value={current}
              onChange={(e) => setCurrent(e.target.value)} required className="mt-1.5" autoFocus />
          </div>
          <div>
            <Label htmlFor="fpc-new">New password</Label>
            <Input id="fpc-new" data-testid="fpc-new" type="password" value={next}
              onChange={(e) => setNext(e.target.value)} required minLength={6} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="fpc-confirm">Confirm new password</Label>
            <Input id="fpc-confirm" data-testid="fpc-confirm" type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required minLength={6} className="mt-1.5" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-[#003057] hover:bg-[#00223f]" data-testid="fpc-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & continue"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
